/* eslint-disable react/jsx-props-no-spreading */
import { faCaretDown, faDownload, faFloppyDisk, faSquareDashedCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, NavLink, useNavigate } from 'react-router-dom';

import { getMultiple } from '../../api/ventura/contract';
import { create, download, update } from '../../api/ventura/reportTemplates';
import ReportTemplateIconFooter from '../../assets/report-template-icon-footer.svg';
import ReportTemplateIconGlossary from '../../assets/report-template-icon-glossary.svg';
import ReportTemplateIconHeader from '../../assets/report-template-icon-header.svg';
import ReportTemplateIconLogo from '../../assets/report-template-icon-logo.svg';
import { REPORTING_HUB_EDIT_URL, REPORTING_HUB_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSnackBar } from '../../hooks/useSnackBar';
import mapReportTemplateToForm from '../../util/mapReportTemplateToForm';
import ReportTemplateSchema, { ReportTemplate } from '../../validationSchemas/ReportTemplateSchema';
import Checkbox from '../forms/Checkbox';
import FormEndOptions from '../forms/FormEndOptions';
import TextFieldEditable from '../forms/TextFieldEditable';
import Configuration from './Configuration';
import Contracts from './Contracts';
import DateFilteringControl from './DateFilteringControl';
import Sheets from './Sheets';
import { ReportingHubContext } from './useReportingHubContext';

const DOWNLOAD_ABORT_MS = 30000;

interface ReportingHubFormProps {
  reportTemplate: ReportTemplate;
}

function ReportingHubForm({ reportTemplate }: ReportingHubFormProps) {
  const navigate = useNavigate();
  const isEdit = !!reportTemplate.templateId;
  const [user] = useLocalStorage('user', null);
  const { id: userId } = user;
  const { apiHost } = useConfig();
  const form = useForm({
    resolver: zodResolver(ReportTemplateSchema),
    defaultValues: {
      ...reportTemplate,
      userId,
    },
  });
  const reportSettingsAnchor = useRef();
  const [isDownloading, setIsDownloading] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const tabList = ['Contracts', 'Configuration'];
  const [activeTab, setActiveTab] = useState(tabList[0]);
  const { showSnackBar } = useSnackBar();

  const togglePopper = (e) => {
    e.stopPropagation();
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(e.target);
    }
  };

  const {
    formState: { isDirty, isValid },
    setValue,
    handleSubmit,
    getValues,
    watch,
    reset,
  } = form;

  if (!isEmpty(form.formState?.errors) || !isValid) {
    // eslint-disable-next-line no-console
    console.log(getValues());
    // eslint-disable-next-line no-console
    console.log(form.formState?.errors);
  }

  useConfirmNavChange(() => isDirty);

  const [selectedContracts, setSelectedContracts] = useState([]);

  useEffect(() => {
    getMultiple(apiHost, reportTemplate.venturaContractIds).then((defaultContract) => {
      setSelectedContracts(defaultContract);
    });
  }, [apiHost, reportTemplate.venturaContractIds, setSelectedContracts]);

  const handleSelectContracts = (newSelectedContracts, replace = false) => {
    const finalSelectedContracts = replace ? newSelectedContracts : concat(selectedContracts, newSelectedContracts);
    setSelectedContracts(finalSelectedContracts);
    setValue(
      'venturaContractIds',
      finalSelectedContracts.map((item) => item.id),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const updateMutation = useMutation({
    mutationFn: (formData: ReportTemplate) =>
      // If user is trying to edit a template that does not belong to them, it will end up creating one for them.
      isEdit && formData.userId === userId ? update(apiHost, userId, formData) : create(apiHost, userId, formData),
    onSuccess: (response) => {
      if (response.model.id) {
        reset(mapReportTemplateToForm(response.model.template.report_template));
      }
      if (!isEdit) {
        showSnackBar('The report template was successfully created.', 'success', {
          event: 'report_template_created',
          template_id: response.model.id,
        });
        setTimeout(
          () => navigate(generatePath(REPORTING_HUB_EDIT_URL, { reportTemplateId: response.model.id.toString() })),
          1000
        );
      } else {
        showSnackBar('The report template was successfully updated.', 'success', {
          event: 'report_template_updated',
          template_id: response.model.id,
        });
      }
    },
    onError: (error: Error) => {
      showSnackBar(error.message, 'error', { event: 'report_template_save_error' });
    },
  });

  const submitHandler = (formData) => {
    updateMutation.mutate(formData);
  };

  const venturaContractIds = watch('venturaContractIds');
  const isValidForDownload = isValid && venturaContractIds?.length > 0;
  const sheetObjects = watch('sheetObjects');
  const [activeSheet, setActiveSheet] = useState(sheetObjects[0]);
  const [activeSection, setActiveSection] = useState(undefined);
  const templateId = watch('templateId');

  const downloadHandler = async () => {
    showSnackBar(
      'Your report will be emailed to you. You can safely leave this page. Quick reports may download here as well.',
      'info',
      { event: 'report_downloaded', template_id: templateId, contract_count: venturaContractIds?.length }
    );
    let errorMessage = 'An unexpected error has occurred. Engineering has been alerted and will investigate';
    let errorEvent = 'report_download_error';
    let snackbarType: 'success' | 'info' | 'warning' | 'error' = 'error';
    try {
      setIsDownloading(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), DOWNLOAD_ABORT_MS);
      const response = await download(apiHost, templateId, { signal: controller.signal });
      clearTimeout(timeoutId);

      setIsDownloading(false);

      if (response?.success && response.link?.indexOf('http') === 0) {
        window.open(response.link);
        return;
      }

      if (response?.error_status) {
        const { error_status: errorStatus } = response;

        switch (errorStatus) {
          case 'NO_CONTRACT_INFO':
            errorMessage = 'No contract info found, double check if the campaign was created today';
            break;
          case 'NO_CONTRACT_SPEND':
            errorMessage = 'No spend found for selected contract(s), double check if campaign has launched';
            break;
          case 'CONTRACT_NOT_ACTIVE':
            errorMessage =
              'Contract start date in the future. If the contract is live, please check with engineering on data delays';
            break;
          default:
            break;
        }
        errorEvent = `report_download_error_${errorStatus.toLowerCase()}`;
      }
    } catch (e) {
      setIsDownloading(false);
      if (e.name === 'AbortError') {
        errorMessage = 'Your report will be emailed when completed.';
        snackbarType = 'info';
      } else {
        errorMessage = e.message;
      }
    }
    showSnackBar(errorMessage, snackbarType, { event: errorEvent, template_id: templateId });
  };

  // Make sure if there are changes to sheetObjects, that we update the active sheet
  useEffect(() => {
    if (activeSheet) {
      const activeSheetIndex = sheetObjects.map((sheetItem) => sheetItem.id).indexOf(activeSheet.id);
      setActiveSheet(sheetObjects[activeSheetIndex]);
    }
  }, [sheetObjects, setActiveSheet, activeSheet]);

  const value = useMemo(
    () => ({
      activeSheet,
      setActiveSheet,
      activeSection,
      setActiveSection,
    }),
    [activeSheet, setActiveSheet, activeSection, setActiveSection]
  );

  return (
    <FormProvider {...form}>
      <ReportingHubContext.Provider value={value}>
        <form onSubmit={handleSubmit(submitHandler)}>
          <Stack
            width="100%"
            height="calc(100% - 70px)"
            direction="row"
            justifyContent="space-between"
            bgcolor="#f4f4f4"
          >
            <Stack p={2} width="70%" borderRight="1px solid #eeeeee" sx={{ overflowY: 'scroll' }}>
              <Stack direction="row" justifyContent="space-between">
                <Stack sx={{ height: '54px' }}>
                  <TextFieldEditable label="Name" name="reportName" sx={{ width: '240px' }} size="small" />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <DateFilteringControl />
                  <Stack>
                  <Button
                    variant="outlined"
                    startIcon={
                      <FontAwesomeIcon icon={faSquareDashedCirclePlus} color="#2D2D2D" style={{ fontSize: '16px' }} />
                    }
                    onClick={togglePopper}
                    ref={reportSettingsAnchor}
                    color="info"
                    endIcon={<FontAwesomeIcon icon={faCaretDown} color="#2D2D2D" style={{ fontSize: '12px' }} />}
                  >
                    &nbsp;Report Settings&nbsp;
                  </Button>
                  <Popper
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    placement="bottom-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                      <div>
                        <Paper sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            <Stack direction="row">
                              <Checkbox label="" name="includeLogo" inputProps={{ 'aria-label': 'Logo' }} />
                              <img src={ReportTemplateIconLogo} alt="Logo Icon" style={{ marginLeft: '-16px' }} />
                              <Typography p="9px">Logo</Typography>
                            </Stack>
                            <Stack direction="row">
                              <Checkbox label="" name="includeHeader" inputProps={{ 'aria-label': 'Header' }} />
                              <img src={ReportTemplateIconHeader} alt="Logo Icon" style={{ marginLeft: '-16px' }} />
                              <Typography p="9px">Header</Typography>
                            </Stack>
                            <Stack direction="row">
                              <Checkbox label="" name="includeFooter" inputProps={{ 'aria-label': 'Footer' }} />
                              <img src={ReportTemplateIconFooter} alt="Logo Icon" style={{ marginLeft: '-16px' }} />
                              <Typography p="9px">Footer</Typography>
                            </Stack>
                            <Stack direction="row">
                              <Checkbox label="" name="includeGlossary" inputProps={{ 'aria-label': 'Glossary' }} />
                              <img src={ReportTemplateIconGlossary} alt="Logo Icon" style={{ marginLeft: '-16px' }} />
                              <Typography p="9px">Glossary</Typography>
                            </Stack>
                          </Stack>
                        </Paper>
                      </div>
                    </ClickAwayListener>
                  </Popper>
                  </Stack>
                </Stack>
              </Stack>
              <Sheets />
            </Stack>
            <Stack width="30%" bgcolor="#f9f9f9">
              <Tabs value={activeTab} variant="fullWidth">
                {tabList.map((tab) => (
                  <Tab
                    key={tab}
                    label={tab}
                    value={tab}
                    sx={{ textTransform: 'none' }}
                    onClick={() => setActiveTab(tab)}
                  />
                ))}
              </Tabs>
              {activeTab === 'Contracts' && <Contracts options={selectedContracts} onChange={handleSelectContracts} />}
              {activeTab === 'Configuration' && <Configuration />}
            </Stack>
          </Stack>
          <FormEndOptions position="fixed">
            <NavLink to={REPORTING_HUB_URL}>
              <Button variant="outlined">Cancel</Button>
            </NavLink>
            <Button
              type="submit"
              name="save"
              disabled={!isDirty || !isValid}
              aria-label={`${isValid ? 'Enabled' : 'Disabled'} Save Template`}
              startIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
            >
              Save Template
            </Button>
            <Button
              name="download"
              disabled={isDirty || !isValidForDownload || isDownloading}
              onClick={downloadHandler}
              startIcon={isDownloading ? <CircularProgress size="1rem" /> : <FontAwesomeIcon icon={faDownload} />}
            >
              {isDownloading ? 'In progress ...' : 'Download and Email Report'}
            </Button>
          </FormEndOptions>
        </form>
      </ReportingHubContext.Provider>
    </FormProvider>
  );
}

export default ReportingHubForm;
